/**
 * ScoreDisplay component — Brand Book §2.9
 * Large mono number with /100 denominator
 * Score zone color mapping: 6-tier system with good (70-79) = teal
 * Uses shared zone utilities from analysis/types.ts
 */

import { clsx } from 'clsx';
import { getScoreZone as getZone, zoneTextColor, zoneLabel } from '../analysis/types';

export interface ScoreDisplayProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

export function ScoreDisplay({
  score,
  size = 'md',
  showLabel = true,
  className,
}: ScoreDisplayProps) {
  const zone = getZone(score);
  const color = zoneTextColor[zone];
  const label = zoneLabel[zone];

  const sizeStyles = {
    sm: 'text-mono-base',
    md: 'text-mono-lg',
    lg: 'text-display',
  };

  const denomStyles = {
    sm: 'text-[10px]',
    md: 'text-mono-sm',
    lg: 'text-mono-base',
  };

  return (
    <div className={clsx('flex flex-col items-center', className)}>
      <div className="flex items-baseline">
        <span
          className={clsx(
            'font-mono font-bold tabular-nums',
            sizeStyles[size],
            color
          )}
        >
          {score}
        </span>
        <span className={clsx('font-mono text-txt-tertiary ml-0.5', denomStyles[size])}>
          /100
        </span>
      </div>
      {showLabel && (
        <span className={clsx('text-caption font-medium mt-1', color)}>
          {label}
        </span>
      )}
    </div>
  );
}

// Re-export zone utilities for backward compatibility
export { getScoreZone as getZone, zoneTextColor, zoneLabel } from '../analysis/types';